// atlas-navbar.js entry point: assemble window.atlas from real browser globals and bootstrap.
//
// Wires the pure modules (config/auth/api/preferences/actions) to the live document, window,
// fetch, crypto, and sessionStorage. Kept thin — all logic lives in the unit-tested modules.

import { ApiClient } from "./api";
import { Auth, isSafeReturn } from "./auth";
import { INIT_TYPE, makeActionApi } from "./actions";
import { makeAppConfig } from "./appConfig";
import { makePreferences } from "./preferences";
import { makeWorkflowApi } from "./workflow";
import { resolveConfig } from "./config";
import { makeCookieStorage } from "./cookie-storage";
import type { ActionMessage, Atlas, RoutingMode, StorageLike } from "./types";

// Identify the current app from the URL. "path" mode (default): first path segment. "subdomain"
// mode: first hostname label (the app subdomain on a wildcard custom domain). The platform apex /
// localhost / a bare host with no label yields "" (the default app / Console).
export function resolveCurrentApp(
  location: { hostname?: string; pathname?: string },
  mode?: RoutingMode,
): string {
  if (mode === "subdomain") {
    const host = (location.hostname || "").split(":")[0];
    const labels = host.split(".");
    // Need at least <app>.<something>.<tld>; a bare host (localhost) or apex has no app label.
    return labels.length >= 3 ? labels[0].trim() : "";
  }
  return ((location.pathname || "").split("/").filter(Boolean)[0] || "").trim();
}

// Route the shared token cache to `tokenStore` (cookie in subdomain mode) but keep the short-lived
// PKCE verifier / CSRF nonce / post-login target in origin-scoped `localStore` (sessionStorage) —
// those must NEVER land in the parent-domain cookie (they'd be readable by every app mid-login).
function makeSplitStorage(tokenStore: StorageLike, localStore: StorageLike): StorageLike {
  const SHARED = "atlas.tokens";
  return {
    getItem: (k) => (k === SHARED ? tokenStore.getItem(k) : localStore.getItem(k)),
    setItem: (k, v) => (k === SHARED ? tokenStore.setItem(k, v) : localStore.setItem(k, v)),
    removeItem: (k) => (k === SHARED ? tokenStore.removeItem(k) : localStore.removeItem(k)),
  };
}

export function bootstrap(win: Window = window): Atlas {
  const config = resolveConfig(win);

  // Token storage: in the custom-domain family use a cookie scoped to the parent domain so one login
  // (completed on the apex auth origin) is shared across every <app>.<domain> — the foundation for
  // unlimited subdomains behind ONE Cognito callback. Elsewhere (path mode, *.cloudfront.net,
  // localhost) keep per-origin sessionStorage exactly as before. The PKCE verifier/CSRF nonce always
  // stay in sessionStorage (origin-scoped, never in the shared cookie) — see auth.ts/cookie-storage.ts.
  const tokenStorage: StorageLike = config.sharedSession
    ? makeCookieStorage({ doc: win.document, domain: config.cookieDomain })
    : win.sessionStorage;

  const auth = new Auth({
    config,
    location: win.location,
    fetch: win.fetch.bind(win) as unknown as import("./types").FetchLike,
    crypto: win.crypto,
    storage: makeSplitStorage(tokenStorage, win.sessionStorage),
  });

  const api = new ApiClient({
    config,
    auth,
    fetch: win.fetch.bind(win) as unknown as import("./types").FetchLike,
    location: win.location,
  });

  // Current app name, used to default preference/config calls. Path mode (default): first path
  // segment (/<app>/...). Subdomain mode: first hostname label (<app>.<domain>).
  const currentApp = resolveCurrentApp(win.location, config.routingMode);

  // Vendor side: cache the parent's init message so getState()/isAction() can read it.
  let initMessage: ActionMessage | null = null;
  win.addEventListener("message", (event) => {
    const data = (event as MessageEvent).data as ActionMessage;
    if (data && data.type === INIT_TYPE) initMessage = data;
  });

  const actions = makeActionApi({
    doc: win.document,
    parentWindow: win.parent,
    appOrigin: win.location.origin,
    addMessageListener(handler) {
      const listener = (event: MessageEvent) => handler(event as unknown as { data: ActionMessage; source?: unknown });
      win.addEventListener("message", listener);
      return () => win.removeEventListener("message", listener);
    },
    getInitMessage: () => initMessage,
    // Routing mode + apex domain so run() builds the correct action-page URL: in subdomain mode it
    // addresses the action's app by its own subdomain (<app>.<appDomain>), avoiding the double
    // "/<app>/<app>/" prefix the viewer-request rewrite would otherwise produce.
    routingMode: config.routingMode,
    appDomain: config.appDomain,
    // The authed requester so action.search() can read the /actions catalog.
    api,
  });

  const atlas: Atlas = {
    config,
    auth: {
      isAuthenticated: () => auth.isAuthenticated(),
      login: (target) => auth.login(target || win.location.href),
      logout: () => auth.logout(),
      getIdToken: () => auth.getIdToken(),
      // Decoded ID-token claims (sub, cognito:groups, ...) for role-aware UI; null when logged out.
      // UX signal only — authorization is enforced server-side, never on these unverified claims.
      getClaims: () => auth.getClaims(),
    },
    preferences: makePreferences(api, currentApp),
    appConfig: makeAppConfig(api, currentApp),
    action: actions,
    workflow: makeWorkflowApi(api, actions),
  };

  // Login handoff from an app subdomain. In subdomain mode, an app that needs auth bounces the user
  // to the apex auth origin as `apex/?atlas_login=1&return=<app-url>` (auth.ts login()). Here on the
  // apex we complete it: if we already hold a shared session, redirect straight back to the (safe)
  // return URL — no Cognito round-trip; otherwise start PKCE, which reads the same `?return=` and
  // folds it into the OAuth `state` so the user lands back on their app after signing in.
  const loginParams = new URLSearchParams(win.location.search || "");
  if (loginParams.get("atlas_login") === "1" && !auth.hasCallbackCode()) {
    const ret = loginParams.get("return");
    if (auth.isAuthenticated() && isSafeReturn(ret, config.appDomain)) {
      win.location.assign(ret!);
    } else {
      auth.login(ret || win.location.href);
    }
    win.atlas = atlas;
    return atlas;
  }

  // Complete a login redirect if we've come back from the Hosted UI with ?code=...
  if (auth.hasCallbackCode()) {
    auth.handleRedirectCallback().then(
      () => {
        // Strip ?code=&state= so a refresh doesn't replay an already-consumed auth code.
        if (win.history && win.history.replaceState) {
          win.history.replaceState({}, "", win.location.pathname);
        }
        const target = win.sessionStorage.getItem("atlas.target");
        if (target) {
          win.sessionStorage.removeItem("atlas.target");
          win.location.assign(target);
        } else {
          // No stored target: reload so the app re-renders authenticated.
          win.location.reload();
        }
      },
      (err) => win.console && win.console.error("atlas login failed", err),
    );
  }

  win.atlas = atlas;
  return atlas;
}

// Auto-bootstrap when served in a browser (the IIFE bundle runs this).
if (typeof window !== "undefined" && window.document) {
  bootstrap(window);
}
